import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { UnrecognizedUserPropertyWidget } from './UnrecognizedUserPropertyWidget';
import { UserManagementPropertyListModal } from '../../modal/UserManagementPropertyListModal';

import {
  HeadWrapper,
  LoadingConstants,
  LoadingStates,
  RentalManagerUserManagementCompanyWebApiHandler_getUnrecognizedUsers,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';
import type {
  UserManagementCompanyPropertyDetailsDto,
  UserManagementUnrecognizedUserDto,
} from '@zg-rentals/rental-manager-env';

import type { ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable } from '@zg-rentals/advanced-table';
import { Col, Row } from '@zg-rentals/zrs-comet';
import { Box, Text, TextButton } from '@zillow/constellation-10';

import { styledTopBannerStyles } from './StyledComponents';
import { Redirect } from 'react-router-dom';
import UserManagementActions from '../../redux/actions/UserManagementActions';

const UnrecognizedUsersPage = () => {
  const paths = useRmwPaths();
  const dispatch = useDispatch();
  const [modalPreviewProperties, setModalPreviewProperties] = useState<Array<UserManagementCompanyPropertyDetailsDto>>(
    [],
  );
  const [showPropertyListModal, setShowPropertyListModal] = useState(false);
  const [unrecognizedUsers, setUnrecognizedUsers] = React.useState<Array<UserManagementUnrecognizedUserDto>>([]);
  const [loadingState, setLoadingState] = React.useState(LoadingStates.INITIAL);

  React.useEffect(() => {
    setLoadingState(LoadingStates.LOADING);
    RentalManagerUserManagementCompanyWebApiHandler_getUnrecognizedUsers()
      .then((result) => {
        setUnrecognizedUsers(result.unrecognizedUsers);
        setLoadingState(LoadingStates.SUCCESS);
        return;
      })
      .catch(() => {
        setLoadingState(LoadingStates.ERROR);
      });
  }, []);

  const previewProperties = (listings: Array<UserManagementCompanyPropertyDetailsDto>) => {
    setModalPreviewProperties(listings);
    setShowPropertyListModal(true);
  };

  const handleAddUser = (email: string) => {
    dispatch(UserManagementActions.currentUserSetEmail(email));
    <Redirect to={`${paths.userManagementPage}/${paths.userManagementAddUser}`} />;
  };

  const columnConfig: Array<ColumnConfig<UserManagementUnrecognizedUserDto>> = [
    {
      width: 5,
      header: 'Email',
      dataKey: 'email',
      align: 'left',
    },
    {
      width: 5,
      header: 'Properties',
      align: 'left',
      renderer: (user) => <UnrecognizedUserPropertyWidget previewProperties={previewProperties} user={user} />,
    },
    {
      width: 2,
      header: 'Invite',
      align: 'right',
      renderer: ({ email }: { email: string }) => <TextButton onClick={() => handleAddUser(email)}>Invite</TextButton>,
    },
  ];

  const renderEmptyBanner = () => {
    if (unrecognizedUsers.length === 0 && loadingState === LoadingConstants.SUCCESS) {
      return (
        <Row hidden={[]}>
          <Col>
            <Text className="empty-message">No Unrecognized users found</Text>
          </Col>
        </Row>
      );
    }
    return null;
  };

  const renderTopBanner = () => (
    <Box className={styledTopBannerStyles}>
      The following emails have permissions to one or more listings in your company. You can invite them to join your
      company as users.
    </Box>
  );

  return (
    <div className="ActivityHistoryPage">
      <HeadWrapper title="Unrecognized Users" />
      <AdvancedTable
        columnConfig={columnConfig}
        data={unrecognizedUsers}
        isLoading={loadingState === LoadingConstants.LOADING || loadingState === LoadingConstants.INITIAL}
        topBannerComponent={renderTopBanner}
      />
      {renderEmptyBanner()}
      {showPropertyListModal && (
        <UserManagementPropertyListModal
          properties={modalPreviewProperties.map((property) => ({
            ...property.address,
            name: property.name,
          }))}
          onClose={() => {
            setShowPropertyListModal(false);
            setModalPreviewProperties([]);
          }}
        />
      )}
    </div>
  );
};

export default UnrecognizedUsersPage;
